"use client";
import { MenuType } from "../../types/baseTypes";

import React, { useState } from "react";
import {
  optionsFooterColumnsType,
  OptionsFooterType,
  OptionsSocialMediaType,
} from "@/types/OptionType";
import styles from "./Footer.module.scss";
import { Contact } from "./Contact/Contact";
import { Socials } from "./Socials/Socials";
import FooterMenu from "./FooterMenu/FooterMenu";
import { CustomLink } from "../Global/CustomLink";
import { ConsentManagement } from "@aleksundshantu.com/aunds-cookie-plugin-react";
import { MediaItem } from "../Global/MediaItem/MediaItem";
import { passACFLink } from "@/utils/passACFLink";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { TextGraphic } from "@/components/Footer/TextGraphic/TextGraphic.tsx";

// TODO and only pass props that are actually used not whole options object and not whoel menu object
interface FooterProps {
  legalMenu?: MenuType;
  footerMenu?: MenuType;
  autoShowConsentManagement?: boolean;
  footerOptions: OptionsFooterType;
  socialMedia: OptionsSocialMediaType;
}

export const Footer = (props: FooterProps) => {
  return <DesktopFooter {...props} />;
};

const FooterColumn = ({
  type,
  columnData,
  footerMenu,
  socialMedia,
  openCookieSettings,
}: {
  type:
    | "logos"
    | "contact"
    | "navigation-links"
    | "social-icons"
    | "social-icons-with-links"
    | "text-graphic";
  columnData: optionsFooterColumnsType;
  footerMenu: MenuType | undefined;
  socialMedia: OptionsSocialMediaType;
  openCookieSettings: () => void;
}) => {
  const { getLabel } = useGlobalContext();

  switch (type) {
    case "logos": {
      return (
        <div className={`${styles["footer-logos"]}`}>
          {Array.isArray(columnData.logos_footer) &&
            columnData.logos_footer.map((el, i) => {
              const { logo, link } = el;
              return link && link.url ? (
                <>
                  <CustomLink
                    key={"footer-logo-" + i}
                    className={`${styles["footer-logo"]}`}
                    {...passACFLink(el, "link")}
                  >
                    <MediaItem
                      image_desktop={logo}
                      sizes="(max-width: 1023px) 100vw, 50vw"
                      className={styles["logo"]}
                      objectFit="contain"
                    />
                  </CustomLink>
                </>
              ) : (
                <div
                  key={"footer-logo-" + i}
                  className={`${styles["footer-logo"]}`}
                >
                  <MediaItem
                    image_desktop={logo}
                    sizes="(max-width: 1023px) 100vw, 50vw"
                    className={styles["logo"]}
                    objectFit="contain"
                  />
                  <p>{getLabel("footer_copyright")}</p>
                </div>
              );
            })}
        </div>
      );
    }
    case "contact": {
      return (
        <div className={`${styles["footer-contact"]}`}>
          <Contact
            contact={{
              title: columnData.contact.title,
              content: columnData.contact.content,
            }}
          />
        </div>
      );
    }
    case "navigation-links": {
      return (
        <div className={`${styles["footer-menu"]}`}>
          <FooterMenu
            menu={footerMenu}
            headline={columnData.navigations_links_title}
            openCookieSettings={openCookieSettings}
          />
        </div>
      );
    }
    case "social-icons": {
      return (
        <div className={`${styles["footer-socials"]}`}>
          <Socials
            platforms={socialMedia.platforms}
            headline={socialMedia.social_media}
          />
        </div>
      );
    }
    case "social-icons-with-links": {
      return (
        <div className={`${styles["footer-socials"]}`}>
          <Socials
            platforms={socialMedia.platforms}
            headline={socialMedia.social_media}
            showName
          />
        </div>
      );
    }
    case "text-graphic": {
      return !columnData.topfi_graphic ? null : (
        <div className={`${styles["footer-text-graphic"]}`}>
          <TextGraphic
            svg={columnData.topfi_graphic.svg}
            content={columnData.topfi_graphic.content}
          />
        </div>
      );
    }
    default: {
      return <></>;
    }
  }
};

const DesktopFooter = ({
  footerMenu,
  socialMedia,
  footerOptions,
  autoShowConsentManagement,
}: FooterProps) => {
  const [showConsentManagement, setShowConsentManagement] = useState(false);
  const { getLabel } = useGlobalContext();

  const {
    navigation,
    column_1,
    column_2,
    column_3,
    column_4,
    footer_structure,
  } = footerOptions || {};

  function calculateFooterColumnWidth() {
    let numberColumns = 0;
    if (footer_structure?.column_1?.show_column) numberColumns++;
    if (footer_structure?.column_2?.show_column) numberColumns++;
    if (footer_structure?.column_3?.show_column) numberColumns++;
    if (footer_structure?.column_4?.show_column) numberColumns++;
    if (numberColumns === 4) return "25%";
    if (numberColumns === 3) return "33.33%";
    if (numberColumns === 2) return "50%";
    if (numberColumns === 1) return "100%";
    return "100%";
  }

  return (
    <footer
      className={`footer is-clipped ${styles.footer} ${navigation.footer_menu.show_line ? styles["footer-with-line"] : ""}`}
      aria-label="footer"
      id="kontakt"
      style={
        {
          "--footer-column-width": `${calculateFooterColumnWidth()}`,
        } as React.CSSProperties
      }
    >
      <div className={"container"}>
        <div className={styles["footer-wrapper"]}>
          <div className={`${styles["footer-top"]}`}>
            {footer_structure?.column_1?.show_column && column_1 && (
              <FooterColumn
                type={footer_structure.column_1.type}
                columnData={column_1}
                footerMenu={footerMenu}
                socialMedia={socialMedia}
                openCookieSettings={() => setShowConsentManagement(true)}
              />
            )}
            {footer_structure?.column_2?.show_column && column_2 && (
              <FooterColumn
                type={footer_structure.column_2.type}
                columnData={column_2}
                footerMenu={footerMenu}
                socialMedia={socialMedia}
                openCookieSettings={() => setShowConsentManagement(true)}
              />
            )}
            {footer_structure?.column_3?.show_column && column_3 && (
              <FooterColumn
                type={footer_structure.column_3.type}
                columnData={column_3}
                footerMenu={footerMenu}
                socialMedia={socialMedia}
                openCookieSettings={() => setShowConsentManagement(true)}
              />
            )}
            <ConsentManagement
              wpApiUrl={process.env.WP_API_URL!}
              reloadOnSave
              forceShow={showConsentManagement}
              autoShow={autoShowConsentManagement ?? true}
            />
          </div>
          <div className={`${styles["footer-bottom"]}`}>
            {footer_structure?.column_4?.show_column && column_4 && (
              <FooterColumn
                type={footer_structure.column_4.type}
                columnData={column_4}
                footerMenu={footerMenu}
                socialMedia={socialMedia}
                openCookieSettings={() => setShowConsentManagement(true)}
              />
            )}

            <div className="backtotop">
              {/* eslint-disable-next-line react/forbid-elements */}
              <a
                href="#pagestart"
                aria-label={getLabel("aria_label_back_to_top")}
              >
                <i className="arrow up"></i>
                {getLabel("back_to_top", "Back to top")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
